import { useEffect, useRef } from "react";
import type { WsEventName } from "@wato/event-bus";
import { useRealtimeConnectionState, useRealtimeEvent } from "./realtime";
import { usePolling } from "./usePolling";

/** Chatty events that never change a counter (typing, presence, delivery ticks, flow counters). */
const IGNORED_EVENTS: ReadonlySet<WsEventName> = new Set([
  "typing",
  "message.status",
  "flow.presence.update",
  "flow.counters.update",
  "conversation.presence.update",
  "conversation.whisper",
]);

/** Bursts of events collapse into one refresh. */
export const REALTIME_REFRESH_DEBOUNCE_MS = 2_000;

/**
 * OWN-440, the wallet chip pattern for server-side counters (notification bell, assistant dot):
 * while the realtime socket is open nothing is polled — `refresh` runs (debounced) when a
 * relevant realtime event arrives. Only while the socket is not open does it fall back to
 * polling every `ms` (also paused while the tab is hidden, via `usePolling`).
 */
export function useRealtimeFallbackPolling(refresh: () => unknown, ms: number): void {
  const connection = useRealtimeConnectionState();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useRealtimeEvent((envelope) => {
    if (IGNORED_EVENTS.has(envelope.event)) return;
    if (timer.current) return; // a refresh is already scheduled
    timer.current = setTimeout(() => {
      timer.current = null;
      void refreshRef.current();
    }, REALTIME_REFRESH_DEBOUNCE_MS);
  });
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  usePolling(refresh, ms, { enabled: connection !== "open" });
}
